import {
    compositePickerNoResultsFound,
    compositePickerOptionExpanded,
    compositePickerOptionSelected,
    compositePickerOptionCollapsed,
    compositePickerOptionsAvailable,
} from './CompositePicker.locstring.json';
import { expandParentOption } from '../actions/parentOptionIsExpandedActions';
import type CompositePickerStore from '../store/schema/CompositePickerStore';
import defaultFilterFunction from '../utils/defaultFilterFunction';
import getNextSelectableOption from '../utils/getNextSelectableOption';
import getOriginalOptionFromFilteredOption from '../utils/getOriginalOptionFromFilteredOption';
import getPickerOptionForIndex from '../utils/getPickerOptionForIndex';
import getPreviousSelectableOption from '../utils/getPreviousSelectableOption';
import { useId } from '@fluentui/react-hooks';
import { observer } from 'mobx-react-lite';
import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { mergeStyles } from '@fluentui/react/lib/Styling';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import loc, { format } from 'owa-localize';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { isBrowserSafari } from 'owa-user-agent';
import { getPaletteAsRawColors } from 'owa-theme';
import * as React from 'react';
import {
    resetFilteredOptions,
    updateFilteredOptions,
} from '../actions/filteredPickerOptionsActions';
import {
    isParentOption,
    ParentOption,
    PickerOption,
    PickerOptionsSection,
} from '../store/schema/CompositePickerInterfaces';
import {
    onOptionSelected,
    updateNavIndex,
    updateTextFieldValue,
} from '../actions/selectedOptionActions';
import { LiveNotification } from 'owa-live-notification';
import { Icon } from '@fluentui/react/lib/Icon';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./CompositePicker.scss');
const classNames = require('classnames/bind').bind(styles);

var numSearches = 0;
var lastProcessedSearch = 0;

export interface CompositePickerClassNames {
    textField?: string;
    callout?: string;
    pickerContainer?: string;
    optionsContainer?: string;
    optionContainer?: string;
    sectionHeader?: string;
    optionButtonFlexContainer?: string;
    optionButton?: string;
    highlightedOptionButton?: string;
    textFieldRoot?: string;
    textFieldGroup?: string;
    textFieldSuffix?: string;
}

interface PickerOptionsAriaLabels {
    leafOptionsAriaLabel: string;
    parentOptionsAriaLabel: string;
}

export interface CompositePickerProps {
    viewState: CompositePickerStore;
    colorScheme: ColorScheme;
    readonly?: boolean;
    styles?: CompositePickerClassNames;
    onChange: (option: PickerOption) => void;
    onParentOptionExpanded?: (option: ParentOption) => void;
    renderOption?: (option: PickerOption) => JSX.Element;
    defaultValue?: string;
    placeholder?: string;
    pickerAriaLabel?: string;
    useOptionLengthAsTextFieldWidth?: boolean;
    hideParentOptionIfEmptyLeafOptions?: boolean;
    alwaysShowAllSections?: boolean;
    showChevronDownButton?: boolean;
    showCalendarColor?: boolean;
    ariaLabels?: PickerOptionsAriaLabels;
    formatOptionName?: (option: PickerOption) => string;
    filterFunction?: (
        sections: PickerOptionsSection[],
        textFieldValue: string,
        formatOptionName: (option: PickerOption) => string
    ) => Promise<PickerOptionsSection[]>;
}

export interface CompositePickerState {
    isCalloutVisible: boolean;
    isTyping: boolean;
}

export default observer(function CompositePicker(props: CompositePickerProps) {
    props = {
        formatOptionName: option => option?.displayName,
        renderOption: option => <span className={styles.optionText}>{option.displayName}</span>,
        filterFunction: defaultFilterFunction,
        ...props,
    };
    const calendarElement = React.useRef<HTMLDivElement>();
    const textFieldElement = React.useRef<ITextField>();
    const [isCalloutVisible_0, setIsCalloutVisible] = React.useState(false);
    const [isTyping, setIsTyping] = React.useState(false);
    const [liveNotificationLabel, setLiveNotificationLabel] = React.useState<string | null>(null);

    /**
     * Check to know whether to display LiveNotification component for A11Y. This is required because of
     * a VoiceOver issue wherein the aria-activedescendant property is not being honored in Safari.
     * VSO links:
     * https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/86308/
     * https://office.visualstudio.com/APEX/_workitems/edit/4195011
     */
    const useLiveNotification = isHostAppFeatureEnabled('macA11y') || isBrowserSafari();

    /**
     * Selects the value of the text filed and opens the callout
     */
    const onTextFieldClicked = () => {
        if (textFieldElement.current) {
            textFieldElement.current.select();
        }
        showCallout();
    };
    const onTextFieldFocus = () => {
        if (isHostAppFeatureEnabled('macA11y') && !isCalloutVisible) {
            showCallout();
        }
    };
    const onChevronDownButtonClicked = () => {
        if (isCalloutVisible_0) {
            hideCallout();
        } else {
            showCallout();
        }
    };
    const onBlur = () => {
        if (props.viewState.textFieldValue === '') {
            setIsTyping(false);
            if (props.viewState.selectedOption) {
                updateTextFieldValue(
                    props.viewState,
                    props.formatOptionName(props.viewState.selectedOption)
                );
            }
        }
    };
    const onCalloutDismiss = (ev?: any) => {
        let relatedTarget = ev.relatedTarget as HTMLElement;
        if (!relatedTarget) {
            relatedTarget = document.activeElement as HTMLElement;
        }
        // Don't hide the callout when you click on the text field
        if (!relatedTarget || relatedTarget.className.indexOf(styles.textField) === -1) {
            const selectedOption = props.viewState.selectedOption;
            if (isTyping) {
                setIsTyping(false);
                if (selectedOption) {
                    updateTextFieldValue(props.viewState, props.formatOptionName(selectedOption));
                }
            }
            updateNavIndex(props.viewState, selectedOption ? selectedOption.index : -1);
            hideCallout();
        }
    };
    /**
     * Renders each account as a header followed by all the writable calendars for that account and parents/channels
     */
    const renderSections = () => {
        const { filteredOptions } = props.viewState;
        const hasMultipleSections = filteredOptions.length > 1;
        if (filteredOptions.length == 0) {
            return renderNoResultsFound();
        }
        return filteredOptions.map((option, index) => {
            const optionsToRender = [...option.leafOptions, ...option.parentOptions];
            const shouldShowSections =
                option?.leafOptions &&
                (option.leafOptions.length > 0 ||
                    (!props.hideParentOptionIfEmptyLeafOptions && option.parentOptions.length > 0));
            return (
                (props.alwaysShowAllSections || shouldShowSections) && (
                    <div
                        className={classNames(
                            styles.optionContainer,
                            props.styles?.optionContainer
                        )}
                        key={option.account}>
                        {hasMultipleSections && (
                            <div>
                                {index != 0 && <hr className={styles.divider} />}
                                <div
                                    className={classNames(
                                        styles.accountText,
                                        props.styles?.sectionHeader
                                    )}>
                                    {option.account}
                                </div>
                            </div>
                        )}
                        {optionsToRender.length > 0
                            ? optionsToRender.map(renderOption)
                            : renderNoResultsFound()}
                    </div>
                )
            );
        });
    };
    const renderOption = (option: PickerOption) => {
        const optionAsParent = isParentOption(option) ? (option as ParentOption) : null;
        const onClick = optionAsParent
            ? event => onParentOptionClicked(event, optionAsParent)
            : event => onChange(event, option);
        const iconProps = optionAsParent
            ? {
                  iconName: optionAsParent.isExpanded
                      ? ControlIcons.ChevronDownMed
                      : ControlIcons.ChevronRightMed,
                  styles: {
                      root: {
                          fontSize: '12px',
                          color: getPaletteAsRawColors().neutralSecondary + '!important',
                          margin: '0 8px 0 6px',
                      },
                  },
              }
            : null;
        const isOptionSelected =
            option && props.viewState.selectedOption
                ? option.index === props.viewState.selectedOption.index
                : false;
        const highlightedOptionButton = props.styles?.highlightedOptionButton
            ? props.styles.highlightedOptionButton
            : mergeStyles({
                  backgroundColor: getPaletteAsRawColors().themeLighter + '!important',
              });
        const ariaLabel = getPickerOptionLabel(option, optionAsParent, props);
        return (
            option?.displayName && (
                <div key={option.displayName} className={styles.optionButtonParent}>
                    <ActionButton
                        id={getPickerOptionId(option)}
                        ariaLabel={ariaLabel}
                        aria-expanded={optionAsParent ? optionAsParent.isExpanded : undefined}
                        aria-selected={optionAsParent ? undefined : isOptionSelected}
                        role={optionAsParent ? 'button' : 'option'}
                        onClick={onClick}
                        styles={{
                            root: classNames(
                                styles.optionButtonRoot,
                                props.styles?.optionButton,
                                option.index == props.viewState.navIndex
                                    ? highlightedOptionButton
                                    : undefined
                            ),
                            rootHovered: styles.optionButtonRootHovered,
                            flexContainer: props.styles?.optionButtonFlexContainer,
                        }}
                        iconProps={iconProps}>
                        {props.renderOption(option)}
                    </ActionButton>
                    {optionAsParent?.isExpanded && optionAsParent.children.map(renderOption)}
                </div>
            )
        );
    };
    const onParentOptionClicked = (event: any, parent: ParentOption) => {
        event.stopPropagation();
        event.preventDefault();
        if (!parent.isExpanded) {
            props.onParentOptionExpanded?.(parent);
        }
        expandParentOption(props.viewState, parent, !parent.isExpanded);
        updateNavIndex(props.viewState, parent.index);
        setFocusToTextField();
    };
    const onChange = (event: any, pickerOption: PickerOption) => {
        const { viewState, formatOptionName } = props;
        if (pickerOption) {
            props.onChange(pickerOption);
            const originalOption = getOriginalOptionFromFilteredOption(viewState, pickerOption);
            const option = originalOption ? originalOption : pickerOption;
            onOptionSelected(viewState, formatOptionName, option);
        } else {
            updateTextFieldValue(props.viewState, '');
        }
        setIsTyping(false);
        hideCallout();
    };
    const handleSearch = ({}, textFieldValue: string) => {
        const { viewState, filterFunction, formatOptionName } = props;
        const { options } = viewState;
        const searchNum = ++numSearches;

        setIsTyping(true);
        updateTextFieldValue(viewState, textFieldValue);
        updateNavIndex(viewState, 0);

        if (!textFieldValue) {
            resetFilteredOptions(viewState);
            if (useLiveNotification) {
                const liveNotificationLabel = getLiveNotificationLabelOnChange(
                    viewState.filteredOptions,
                    props
                );
                setLiveNotificationLabel(liveNotificationLabel);
            }
            return;
        }

        filterFunction(options, textFieldValue, formatOptionName).then(results => {
            if (searchNum > lastProcessedSearch) {
                lastProcessedSearch = searchNum;
                updateFilteredOptions(viewState, results);
                if (useLiveNotification) {
                    const liveNotificationLabel = getLiveNotificationLabelOnChange(results, props);
                    setLiveNotificationLabel(liveNotificationLabel);
                }
            }
        });
        showCallout();
    };
    const handleKeyPress = (ev: React.KeyboardEvent<HTMLElement>) => {
        const keyCode = ev.which;
        const isShiftKeyPressed = ev.shiftKey;
        switch (keyCode) {
            case KeyCodes.down:
                if (isShiftKeyPressed) {
                    return;
                }
                handleKeyDown(ev);
                return;
            case KeyCodes.up:
                if (isShiftKeyPressed) {
                    return;
                }
                handleKeyUp(ev);
                return;
            case KeyCodes.backspace:
                handleBackspace();
                return;
            case KeyCodes.enter:
                handleEnter(ev);
                return;
            case KeyCodes.escape:
                handleEscape(ev);
                return;
            case KeyCodes.tab:
                handleTab(ev);
                return;
        }
    };
    const handleKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();
        const nextOption =
            getNextSelectableOption(props.viewState, props.viewState.navIndex) ||
            props.viewState.selectedOption;

        if (useLiveNotification) {
            const nextOptionLabel = getLiveNotificationLabel(nextOption, props);
            setLiveNotificationLabel(nextOptionLabel);
        }
        updateNavIndex(props.viewState, nextOption.index);
        scrollToElement(nextOption);
        if (!isCalloutVisible_0 && !isParentOption(nextOption)) {
            onChange(ev, nextOption);
        }
    };
    const handleKeyUp = (ev: React.KeyboardEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();
        const previousOption =
            getPreviousSelectableOption(props.viewState, props.viewState.navIndex) ||
            props.viewState.selectedOption;

        if (useLiveNotification) {
            const previousOptionLabel = getLiveNotificationLabel(previousOption, props);
            setLiveNotificationLabel(previousOptionLabel);
        }
        updateNavIndex(props.viewState, previousOption.index);
        scrollToElement(previousOption);
        if (!isCalloutVisible_0 && !isParentOption(previousOption)) {
            onChange(ev, previousOption);
        }
    };
    const handleEnter = (ev: React.KeyboardEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();
        if (isCalloutVisible_0) {
            setSelectionBasedOnIndex(ev);
        } else {
            showCallout();
        }
    };
    const handleEscape = (ev: React.KeyboardEvent<HTMLElement>) => {
        if (isCalloutVisible_0) {
            ev.stopPropagation();
            ev.preventDefault();
            setSelectionBasedOnState(ev);
        }
    };
    const handleTab = (ev: React.KeyboardEvent<HTMLElement>) => {
        if (isCalloutVisible_0) {
            hideCallout();
        }
    };
    const setFocusToTextField = () => textFieldElement.current?.focus();
    const handleBackspace = () => {
        if (!isTyping) {
            updateTextFieldValue(props.viewState, '');
            resetFilteredOptions(props.viewState);
        }
        setFocusToTextField();
    };
    const hideCallout = () => {
        setIsCalloutVisible(false);
        resetFilteredOptions(props.viewState);
        setLiveNotificationLabel(null);
    };
    const showCallout = () => {
        setIsCalloutVisible(true);
        const { filteredOptions, selectedOption } = props.viewState;
        for (let option of filteredOptions) {
            if (option.parentOptions) {
                for (let parent of option.parentOptions) {
                    expandParentOption(
                        props.viewState,
                        parent,
                        selectedOption?.parentId === parent.id /* parentIsExpanded */
                    );
                }
            }
        }
        setLiveNotificationLabel(loc(compositePickerOptionsAvailable));
    };
    /**
     * Set the selected option based on the current index (current highlighted option)
     */
    const setSelectionBasedOnIndex = (ev: React.KeyboardEvent<HTMLElement>) => {
        const { navIndex } = props.viewState;
        if (navIndex === -1) {
            // Nothing selected, finish early
            return;
        }
        const pickerOption = getPickerOptionForIndex(props.viewState, navIndex);
        if (isParentOption(pickerOption)) {
            onParentOptionClicked(ev, pickerOption as ParentOption);
            return;
        }
        onChange(ev, pickerOption);
    };
    /**
     * Set the selected option to the last option saved to state
     */
    const setSelectionBasedOnState = (ev: React.KeyboardEvent<HTMLElement>) => {
        const pickerOption = props.viewState.selectedOption;
        if (isParentOption(pickerOption)) {
            // can't select; early return
            return;
        }
        onChange(ev, pickerOption);
    };

    const onLayerMounted = () => {
        const option = props.viewState.selectedOption;
        if (option) {
            scrollToElement(option, true /*quick*/);
        }
    };

    const isCalloutVisible = isCalloutVisible_0;
    const isTextFieldReadOnly = props.viewState.options.length == 0 || props.readonly;
    const placeholder = props.placeholder || props.formatOptionName(props.viewState.selectedOption);
    let { textFieldValue } = props.viewState;
    if (isTextFieldReadOnly && props.defaultValue) {
        textFieldValue = props.defaultValue;
    }
    const iconProps =
        !isTextFieldReadOnly && !props.showChevronDownButton
            ? {
                  iconName: ControlIcons.ChevronDown,
                  styles: {
                      root: styles.textFieldChevronIcon,
                  },
              }
            : null;

    const [textFieldWidth, setTextFieldWidth] = React.useState<number>(NaN);
    const textFieldSpanRef = React.useRef<HTMLSpanElement>(null);
    const textFieldValueSpan = !isTyping && textFieldValue ? textFieldValue : placeholder;

    // Use a span to measure the text field width
    // The span renders the same value as the text field and it will be rendered in the 'suffix' slot
    const onRenderSuffix = React.useCallback(
        () => <span ref={textFieldSpanRef}>{textFieldValueSpan}</span>,
        [textFieldSpanRef.current, textFieldValueSpan]
    );

    React.useLayoutEffect(() => {
        if (textFieldSpanRef.current?.clientWidth) {
            setTextFieldWidth(Math.ceil(textFieldSpanRef.current.clientWidth));
        }
    }, [textFieldSpanRef.current, textFieldValueSpan]);

    const textFieldWidthStyle =
        props.useOptionLengthAsTextFieldWidth && textFieldWidth
            ? {
                  width: `${textFieldWidth + 18}px`,
              }
            : undefined;
    const compositePickerCalloutId = useId();
    const showSelectedCalendarColor =
        props.showCalendarColor && isFeatureEnabled('mon-cal-CalendarSelectorColor');

    return (
        <>
            {useLiveNotification && (
                <LiveNotification message={liveNotificationLabel} atomic={true} />
            )}
            <div
                className={classNames(styles.container, props.styles?.pickerContainer)}
                role="combobox"
                aria-expanded={isCalloutVisible}>
                <div
                    ref={calendarElement}
                    className={showSelectedCalendarColor ? styles.topContainer : undefined}>
                    {showSelectedCalendarColor && (
                        <Icon
                            iconName={ControlIcons.CircleFill}
                            styles={{
                                root: {
                                    color: props.colorScheme.calPrimary,
                                },
                            }}
                        />
                    )}
                    <TextField
                        styles={{
                            root: props.styles?.textFieldRoot,
                            fieldGroup: mergeStyles(
                                textFieldWidthStyle,
                                props.styles?.textFieldGroup
                            ),
                            field: classNames(styles.textField, props.styles?.textField),
                            suffix: classNames(styles.optionText, props.styles?.textFieldSuffix),
                        }}
                        placeholder={placeholder}
                        ariaLabel={props.pickerAriaLabel}
                        autoComplete={'off'}
                        componentRef={textFieldElement}
                        borderless={true}
                        readOnly={isTextFieldReadOnly}
                        value={textFieldValue}
                        onFocus={onTextFieldFocus}
                        onClick={onTextFieldClicked}
                        onKeyDown={handleKeyPress}
                        onChange={handleSearch}
                        onBlur={onBlur}
                        iconProps={iconProps}
                        role="searchbox"
                        aria-controls={
                            isCalloutVisible && !useLiveNotification
                                ? compositePickerCalloutId
                                : undefined
                        }
                        aria-activedescendant={
                            isCalloutVisible && !useLiveNotification
                                ? props.viewState.navIndex.toString()
                                : undefined
                        }
                        onRenderSuffix={
                            props.useOptionLengthAsTextFieldWidth ? onRenderSuffix : undefined
                        }
                    />
                    {!isTextFieldReadOnly && props.showChevronDownButton && (
                        <IconButton
                            iconProps={{ iconName: ControlIcons.ChevronDown }}
                            styles={{
                                root: styles.chevronButtonRoot,
                                icon: styles.chevronButtonIcon,
                            }}
                            onClick={onChevronDownButtonClicked}
                            tabIndex={-1}
                            data-is-focusable={false}
                            aria-hidden={true}
                        />
                    )}
                </div>
                {isCalloutVisible && !isTextFieldReadOnly && (
                    <Callout
                        id={compositePickerCalloutId}
                        target={calendarElement.current}
                        className={classNames(styles.calloutContainer, props.styles?.callout)}
                        ariaLabel={props.pickerAriaLabel}
                        onDismiss={onCalloutDismiss}
                        onLayerMounted={onLayerMounted}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        isBeakVisible={false}
                        role="listbox"
                        gapSpace={0}>
                        <div
                            className={classNames(
                                styles.optionsContainer,
                                props.styles?.optionsContainer
                            )}>
                            {renderSections()}
                        </div>
                    </Callout>
                )}
            </div>
        </>
    );
});

/**
 * Renders 'No results found' when search doesn't have any match
 */
function renderNoResultsFound() {
    return <div className={styles.noResultsFound}>{loc(compositePickerNoResultsFound)}</div>;
}

/**
 * Generates an id for each option based on the option type and index
 */
function getPickerOptionId(pickerOption: PickerOption): string {
    if (!pickerOption) {
        return '';
    }

    return pickerOption.index.toString();
}

/**
 * Scrolls to the selected/highlighted option
 */
function scrollToElement(pickerOption: PickerOption, quick?: boolean) {
    const id = getPickerOptionId(pickerOption);
    const element = document.getElementById(id);
    if (element) {
        element.scrollIntoView({
            block: 'center',
            behavior: quick ? 'auto' : 'smooth',
        });
    }
}

function getPickerOptionLabel(
    option: PickerOption,
    optionAsParent: ParentOption,
    props: CompositePickerProps
) {
    return props.ariaLabels
        ? optionAsParent
            ? format(loc(props.ariaLabels.parentOptionsAriaLabel), option.displayName)
            : format(loc(props.ariaLabels.leafOptionsAriaLabel), option.displayName)
        : option.displayName;
}

function getLiveNotificationLabel(option: PickerOption, props: CompositePickerProps) {
    const optionAsParent = isParentOption(option) ? (option as ParentOption) : null;

    const isOptionSelected =
        option && props.viewState.selectedOption
            ? option.index === props.viewState.selectedOption.index
            : false;

    const label = [];

    label.push(getPickerOptionLabel(option, optionAsParent, props));

    if (optionAsParent) {
        if (optionAsParent.isExpanded) {
            label.push(loc(compositePickerOptionExpanded));
        } else {
            label.push(loc(compositePickerOptionCollapsed));
        }
    } else if (isOptionSelected) {
        label.push(loc(compositePickerOptionSelected));
    }

    return label.join(', ');
}

function getLiveNotificationLabelOnChange(
    filteredOptions: PickerOptionsSection[],
    props: CompositePickerProps
) {
    for (const option of filteredOptions) {
        if (option.leafOptions?.length > 0) {
            return getLiveNotificationLabel(option.leafOptions[0], props);
        } else if (option.parentOptions?.length > 0) {
            return getLiveNotificationLabel(option.parentOptions[0], props);
        }
    }
    return null;
}
